import { useMemo } from 'react';
import { buildReferringIpColumns } from '../columnsConfig';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import ApiLoadError from '@/components/errors/ApiLoadError';
import {
  useGetReferringIpsCountQuery,
  useGetReferringIpsMutation
} from '../api/backlinkReferringIpApi';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useExportBacklinkTableMutation } from '../api/exportApi';
import useBacklinkQueryBody from '../hooks/useBacklinkQueryBody';

const ReferringIpPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();
  const [exportTable] = useExportBacklinkTableMutation();
  const body = useBacklinkQueryBody();
  const { ip_prefix_length: ipPrefixLength } = body;
  const columns = useMemo(
    () => buildReferringIpColumns(ipPrefixLength),
    [ipPrefixLength]
  );

  const {
    data: getReferringIpsRes,
    isFetching: getReferringIpsLoading,
    isError: getReferringIpsError,
    error: getReferringIpsErrorDetails,
    refetch: refetchReferringIps
  } = useGetReferringIpsCountQuery({ ...body, page: 1, perPage: 50 });
  const [
    getReferringIpsFunc,
    {
      isError: isReferringIpsMutationError,
      error: referringIpsMutationError,
      isLoading: isReferringIpsMutationLoading,
      reset: resetReferringIpsMutation
    }
  ] = useGetReferringIpsMutation();

  const handleIpPrefixChange = (value: string | number) => {
    const params = new URLSearchParams(searchParams);
    if (String(value) === '24') {
      params.delete('ip_prefix');
    } else {
      params.set('ip_prefix', String(value));
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <>
      <FiltersComponent
        preQueries={searchParams.toString()}
        columns={columns}
        ipPrefixLength={ipPrefixLength}
        onIpPrefixLengthChange={handleIpPrefixChange}
      />

      {!getReferringIpsLoading ? (
        getReferringIpsError ? (
          <ApiLoadError
            error={getReferringIpsErrorDetails}
            isError={getReferringIpsError}
            onRetry={() => refetchReferringIps?.()}
            isRetrying={getReferringIpsLoading}
          />
        ) : isReferringIpsMutationError ? (
          <ApiLoadError
            error={referringIpsMutationError}
            isError={isReferringIpsMutationError}
            onRetry={() => {
              resetReferringIpsMutation?.();
              refetchReferringIps?.();
            }}
            isRetrying={isReferringIpsMutationLoading}
          />
        ) : (
          <AgGridWrapper
            table_name={pathname.split('/').slice(1, 3).join('/')}
            otherOptions={{
              headerHeight: 52
            }}
            settings={{
              pagination: true,
              fullscreen: false,
              changeHeight: false,
              resetGrid: false,
              saveGrid: false,
              sideBar: false,
              export: {
                enabled: true,
                fetchFunction: exportTable,
                additionalData: body
              }
            }}
            stickyHorizontalScroll
            stickyHeader
            height="auto"
            fetchFunction={getReferringIpsFunc}
            total={getReferringIpsRes?.data?.total}
            data={getReferringIpsRes?.data?.rows}
            columns={columns}
            additionalData={body}
          />
        )
      ) : (
        <AgGridSkeleton height="400px" />
      )}
    </>
  );
};

export default ReferringIpPage;
